import { useController } from "react-hook-form";

import TextInput from "@/form/inputs/TextInput/TextInput";
import { useFieldIds } from "@/form/useFieldIds";
import { cn } from "@/lib/utils";

import FieldError from "../FieldError/FieldError";
import FieldHint from "../FieldHint/FieldHint";
import FieldLabel from "../FieldLabel/FieldLabel";

export type TextFieldProps = {
  readonly disabled?: boolean;
  readonly readOnly?: boolean;
  readonly autoFocus?: boolean;
  readonly type?:
    | "text"
    | "tel"
    | "number"
    | "currency"
    | "hidden"
    | "password"
    | "search"
    | "email"
    | "decimal"
    | "datetime-local"
    | "date";
  readonly label: string;
  readonly labelClassName?: string;
  readonly hint?: string;
  readonly placeholder?: string;
  readonly name: string;
  readonly maxLength?: number;
  readonly pattern?: string;
  readonly "data-testid"?: string;
  readonly width?: string;
  readonly height?: string;
  readonly max?: string | number;
  readonly onBlur?: (value: string) => void;
  readonly onKeyUp?: (value: string) => void;
  readonly onChange?: (value: string | number) => void;
  readonly onIconClick?: (side: "left" | "right") => void;
  readonly hideLabel?: boolean;
  readonly hideValidation?: boolean;
  readonly leftIcon?: React.ReactNode;
  readonly rightIcon?: React.ReactNode;
  readonly errorClassname?: string;
};

export default function TextField({
  disabled = false,
  label,
  name,
  hint,
  type,
  placeholder,
  onBlur,
  onChange,
  hideLabel = false,
  hideValidation = false,
  leftIcon,
  rightIcon,
  readOnly = false,
  errorClassname,
}: TextFieldProps): React.ReactElement {
  const { inputId, labelId, hintId, errorId, describedBy } = useFieldIds(name);

  const { field, fieldState } = useController({
    name,
  });

  const invalid = !!fieldState.error;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;

    if (type === "currency") {
      const sanitized = rawValue.replace(/[^0-9.]/g, "");
      const formatted = sanitized.replace(/\..*\./, (match) =>
        match.slice(0, match.indexOf(".") + 1)
      );

      // Limit to 2 decimal places
      const decimalIndex = formatted.indexOf(".");
      const limitedFormatted =
        decimalIndex !== -1 && formatted.length > decimalIndex + 3
          ? formatted.slice(0, decimalIndex + 3)
          : formatted;

      const isPartialDecimal =
        !limitedFormatted ||
        limitedFormatted.endsWith(".") ||
        limitedFormatted === ".";

      const fieldValue = isPartialDecimal
        ? limitedFormatted
        : parseFloat(limitedFormatted) || 0;

      onChange?.(limitedFormatted);
      field.onChange(fieldValue);
    } else {
      const value = type === "number" ? Number(rawValue) : rawValue;
      onChange?.(value);
      field.onChange(value);
    }
  };

  const handleBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    // For numeric text inputs, ensure the final value is a number on blur
    if (type === "currency") {
      const value = e.target.value;
      if (value && typeof field.value === "string") {
        const numericValue = parseFloat(value);
        if (!isNaN(numericValue)) {
          field.onChange(numericValue);
        }
      }
    }
    onBlur?.(e.target.value);
  };

  return (
    <div
      className={cn(
        "TextField grid flex-1 gap-2",
        type === "hidden" && "hidden"
      )}
      data-testid="TextField"
      data-field={name}
    >
      <div className={cn("grid", hideLabel && "sr-only")}>
        <FieldLabel
          htmlFor={inputId}
          id={labelId}
          invalid={invalid}
          value={label}
        />
      </div>

      <TextInput
        id={inputId}
        aria-describedby={describedBy(!!hint, !!fieldState.error)}
        aria-invalid={invalid}
        disabled={disabled}
        leftIcon={leftIcon}
        name={name}
        placeholder={placeholder}
        readOnly={readOnly}
        rightIcon={rightIcon}
        type={type}
        value={field.value ?? ""}
        onBlur={handleBlur}
        onChange={handleChange}
      />

      <div
        className={cn(
          "align-center flex h-3 justify-start",
          hideValidation && "hidden",
          errorClassname
        )}
      >
        {hint && !fieldState.error && <FieldHint hint={hint} id={hintId} />}
        {fieldState.error && (
          <FieldError error={fieldState.error} id={errorId} />
        )}
      </div>
    </div>
  );
}
